import { useEffect, useState } from 'react'
import {
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import {
  COMPUTED_OPERATION_TYPES,
  COMPUTED_RESERVED_KEYS,
  FIELD_TYPES,
} from '../../../utility/constants'
import { listDataService } from '../../../services/listDataService'
import { toast } from 'react-toastify'
import DeleteIcon from '@mui/icons-material/Delete'
import { GroupHeader, GroupItems } from './CreateProcessTemplateDialog'
import AlternateComputationSetupUI from './AlternateComputationSetupUI'
import { getTimeInMillis } from '../../../utility/datetime'

export const PARAM_TYPES = {
  Field: '^^',
  Keyword: '**',
  Constant: '',
}

export const VALUE_POLARITY = {
  '+ve': '+',
  '-ve': '-',
}

export const PROPERTIES = {
  value: 'value',
  id: 'id',
}

export const getKeyOfValue = (obj = {}, val) => {
  const key = Object.entries(obj)?.find(([key, value]) => {
    return value == val
  })

  if (key) {
    return key[0]
  } else {
    return undefined
  }
}

export const getItemFromList = (list, key, value) => {
  return list?.find((it) => it[key] == value)
}

const getParamsFromMetadata = (metadata, fields = []) => {
  const params = []
  const operation = metadata?.operation
  metadata?.params?.forEach((param, index) => {
    if (param?.toString()?.startsWith('^^')) {
      params.push(
        [COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(operation)
          ? {
              id: new Date().getTime() + index,
              type: 'Field',
              field: getItemFromList(fields, 'id', param.substring(3, param.indexOf('.'))),
              polarity: getKeyOfValue(VALUE_POLARITY, param.substring(2, 3)),
              property: param.substring(param.indexOf('.') + 1),
            }
          : {
              id: new Date().getTime() + index,
              type: 'Field',
              field: getItemFromList(fields, 'id', param.substring(2, param.indexOf('.'))),
              polarity: '',
              property: param.substring(param.indexOf('.') + 1),
            }
      )
    } else if (param?.toString()?.startsWith('**')) {
      params.push({
        id: new Date().getTime() + index,
        type: 'Keyword',
        field: getKeyOfValue(COMPUTED_RESERVED_KEYS, param.substring(2)),
      })
    } else {
      params.push({
        id: new Date().getTime() + index,
        type: 'Constant',
        field: param,
      })
    }
  })
  return params
}

export const getParamField = (param, operation) => {
  if (param.type == 'Field') {
    return (
      '' +
      PARAM_TYPES[param.type] +
      ([COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(operation)
        ? VALUE_POLARITY[param.polarity] || '+'
        : '') +
      param?.field?.id +
      '.' +
      param?.property
    )
  } else if (param.type == 'Keyword') {
    return '' + PARAM_TYPES[param.type] + COMPUTED_RESERVED_KEYS[param?.field]
  } else {
    return '' + PARAM_TYPES[param.type] + param?.field
  }
}

const getExclusionsFromMetadata = (metadata, fields = [], listData) => {
  const exclusions = []
  metadata?.exclusions?.forEach((exclusion, index) => {
    const f = fields.find((it) => it.id == exclusion.field_template_id)
    exclusions.push({
      id: new Date().getTime() + index,
      field: f,
      listItem: listData[`list_data_${f?.list_id}`]?.find((it) => it.id == exclusion.list_data_id),
    })
  })
  return exclusions
}

const FieldComputationSetupUI = ({ field, activityTemplateId, processTemplateId, saveChanges }) => {
  const { user } = useAuthContext()
  const [operation, setOperation] = useState('')
  const [computed, setComputed] = useState(field.is_computed)
  const [pivotFieldId, setPivotFieldId] = useState(null)
  const [counterFieldId, setCounterFieldId] = useState(null)
  const [exclusions, setExclusions] = useState([])
  const [params, setParams] = useState([])

  const [altFieldTemplateId, setaltFieldTemplateId] = useState(null)
  const [fields, setFields] = useState([])
  const [allPivotFields, setAllPivotFields] = useState([])
  const [listData, setListData] = useState([])

  const [altComputation, setaltComputation] = useState([])

  useEffect(() => {
    ;(async () => {
      if (user && activityTemplateId) {
        // get all fields
        const allFieldResult = await fieldTemplateService.getDerivableFieldsForProcess(
          user,
          processTemplateId
        )
        setFields(allFieldResult)

        const allPivotFields = await fieldTemplateService.getAllPivotFieldsInProcess(
          user,
          processTemplateId
        )
        setAllPivotFields(allPivotFields?.data || [])

        // get all list items in a activity
        const listData = await listDataService.getDataActivity(user, activityTemplateId)
        setListData(listData)
      }
    })()
  }, [field])

  useEffect(() => {
    // convert metadata in representation format
    const operation = getKeyOfValue(COMPUTED_OPERATION_TYPES, field?.metadata?.operation)

    setOperation(COMPUTED_OPERATION_TYPES[operation])

    if (fields) {
      const defaultParams = getParamsFromMetadata(field?.metadata, fields)

      setParams(defaultParams)
    }

    if (fields && listData) {
      const defaultExclusions = getExclusionsFromMetadata(field?.metadata, fields, listData)
      setExclusions(defaultExclusions)
    }

    if (field?.metadata?.counterFieldId) {
      setCounterFieldId(getItemFromList(fields, 'id', field?.metadata?.counterFieldId)?.id)
    }

    if (field?.metadata?.pivotFieldTemplateId) {
      setPivotFieldId(getItemFromList(fields, 'id', field?.metadata?.pivotFieldTemplateId)?.id)
    }
    if (field?.metadata?.alt_field_template_id) {
      setaltFieldTemplateId(
        getItemFromList(fields, 'id', field?.metadata?.alt_field_template_id)?.id
      )
    }

    if (field?.metadata?.alt_field_template_id) {
      const altComputations = field?.metadata?.alt

      setaltComputation(altComputations || [])
    }
  }, [field, fields, listData])

  const getSelectedField = (fieldId) => {
    const field = fields?.find((it) => it.id == fieldId)
    if (field) {
      return { value: field, label: field?.field_template_name }
    } else {
      return null
    }
  }

  const getSelectedItem = (itemId, listId) => {
    const items = listData[`list_data_${listId}`]
    const item = items?.find((it) => it.id == itemId)
    if (item) {
      return { value: item, label: item?.value }
    } else {
      return null
    }
  }

  const getListItemOptions = (field) => {
    if (field) {
      const items = listData[`list_data_${field?.list_id}`]
      if (items) {
        return items.map((it) => ({ value: it, label: it.value }))
      } else {
        return []
      }
    } else {
      return []
    }
  }

  const addParam = () => {
    setParams([
      ...params,
      {
        id: getTimeInMillis(),
      },
    ])
  }

  const addAltComputation = () => {
    const updated = [
      ...altComputation,
      {
        id: getTimeInMillis(),
      },
    ]

    setaltComputation(updated)
  }

  const addExclusion = () => {
    setExclusions([
      ...exclusions,
      {
        id: getTimeInMillis(),
      },
    ])
  }

  const saveComputation = async () => {
    try {
      const finalParams = params.map((it) => getParamField(it, operation))

      const finalComputationValue = {
        alt: altComputation,
        alt_field_template_id: altFieldTemplateId,
        operation: operation,
        pivotFieldTemplateId: pivotFieldId,
        counterFieldTemplateId: counterFieldId,
        params: finalParams,
        exclusions: exclusions.map((it) => ({
          field_template_id: it?.field?.id,
          list_data_id: it?.listItem?.id,
        })),
      }

      const updatedField = {
        ...field,
        metadata: computed ? finalComputationValue : null,
        is_computed: computed,
        is_read_only: computed,
      }

      saveChanges(updatedField)
      toast.success('Computation added successfully!')
    } catch (error) {
      toast.error(error.message)
    }
  }

  return computed ? (
    <div>
      {/* Is Computed */}
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Is Computed</InputLabel>
        <Select
          style={{ minWidth: 60, marginBottom: 16 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={computed ? 'Yes' : 'No'}
          onChange={(e) => {
            const isComputed = e.target.value == 'Yes'
            setComputed(isComputed)
          }}
          label="Polarity"
        >
          <MenuItem value={'No'}>No</MenuItem>
          <MenuItem value={'Yes'}>Yes</MenuItem>
        </Select>
      </FormControl>
      {/* OPERATION */}
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Operation</InputLabel>
        <Select
          style={{ minWidth: '100%', marginBottom: 16 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={operation}
          onChange={(e) => {
            setOperation(e.target.value)
            if (e.target.value != COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
              setExclusions([])
            }

            if (
              ![COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(
                e.target.value
              )
            ) {
              setParams(params.map((it) => ({ ...it, polarity: '' })))
            }
          }}
          label="Operation"
        >
          {Object.keys(COMPUTED_OPERATION_TYPES).map((it) => {
            return <MenuItem value={COMPUTED_OPERATION_TYPES[it]}>{it}</MenuItem>
          })}
        </Select>
      </FormControl>
      {/*Counter - Pivot Field */}
      {params.find((it) => it.field == 'COUNTER') && (
        <Autocomplete
          style={{ marginBottom: 16 }}
          value={getSelectedField(pivotFieldId || null)}
          id="autocomplete-field"
          options={fields
            .filter((it) => it.is_pivot_field)
            .map((it) => {
              return { value: it, label: it.field_template_name }
            })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Pivot Field" />}
          onChange={(_, v) => {
            setPivotFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}
      {/*Sum Across Process Pivot Field */}
      {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
        <Autocomplete
          style={{ marginBottom: 16 }}
          value={getSelectedField(pivotFieldId || null)}
          id="sum-across-pivot-field"
          options={allPivotFields.map((it) => {
            return { value: it, label: it.field_template_name }
          })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Pivot Field" />}
          onChange={(_, v) => {
            setPivotFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}

      {params.find((it) => it.field == 'COUNTER') && (
        <Autocomplete
          style={{ marginBottom: 20 }}
          value={getSelectedField(counterFieldId || null)}
          id="autocomplete-field"
          options={fields.map((it) => {
            return { value: it, label: it.field_template_name }
          })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Counter Field" />}
          onChange={(_, v) => {
            setCounterFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}
      <p>Params: </p>
      {params.map((param, index) => {
        return (
          <div style={{ display: 'flex', columnGap: 8, marginBottom: 16, alignItems: 'center' }}>
            <p style={{ margin: 0, minWidth: 20 }}>{index + 1}.</p>
            {/* PARAM TYPE */}
            <FormControl variant="outlined" size="small" color="primary">
              <InputLabel id="select-option-label">Param Type</InputLabel>
              <Select
                style={{ minWidth: 120 }}
                size="small"
                color="primary"
                labelId="select-option-label"
                id="select-option"
                value={param.type}
                onChange={(e) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, type: e.target.value } : it
                    })
                  )
                }}
                label="Param Type"
              >
                {Object.keys(PARAM_TYPES).map((it) => {
                  return <MenuItem value={it}>{it}</MenuItem>
                })}
              </Select>
            </FormControl>
            {/* FIELD */}
            {param.type == 'Field' ? (
              <Autocomplete
                sx={{ minWidth: 160 }}
                id="select-param-field"
                options={fields.sort(
                  (a, b) => -b.activity_template_name.localeCompare(a.activity_template_name)
                )}
                groupBy={(option) => option?.activity_template_name}
                size="small"
                getOptionLabel={(item) => item?.field_template_name}
                value={param?.field || null}
                onChange={(_, v) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, field: v } : it
                    })
                  )
                }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select derived field"
                    color="primary"
                  />
                )}
              />
            ) : param.type == 'Keyword' ? (
              <FormControl variant="outlined" size="small" color="primary">
                <InputLabel id="select-option-label">Keyword</InputLabel>
                <Select
                  style={{ minWidth: 160 }}
                  size="small"
                  color="primary"
                  labelId="select-option-label"
                  id="select-option"
                  value={param.field}
                  onChange={(e) => {
                    setParams(
                      params.map((it) => {
                        return it.id == param.id ? { ...param, field: e.target.value } : it
                      })
                    )
                  }}
                  label="Keyword"
                >
                  {Object.keys(COMPUTED_RESERVED_KEYS).map((it) => {
                    return <MenuItem value={it}>{it}</MenuItem>
                  })}
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small"
                value={param.field}
                label="Static Value"
                onChange={(e) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, field: e.target.value } : it
                    })
                  )
                }}
              />
            )}
            {/* Field Properties */}
            {param.type == 'Field' && param.field && (
              <FormControl variant="outlined" size="small" color="primary">
                <InputLabel id="select-option-label">Property</InputLabel>
                <Select
                  style={{ minWidth: 100 }}
                  size="small"
                  color="primary"
                  labelId="select-option-label"
                  id="select-option"
                  value={param.property}
                  onChange={(e) => {
                    setParams(
                      params.map((it) => {
                        return it.id == param.id ? { ...param, property: e.target.value } : it
                      })
                    )
                  }}
                  label="Property"
                >
                  {Object.keys(PROPERTIES).map((it) => {
                    return <MenuItem value={it}>{it}</MenuItem>
                  })}
                </Select>
              </FormControl>
            )}
            {/* Positive or Negative */}
            {param.type == 'Field' &&
              param.field &&
              (operation == COMPUTED_OPERATION_TYPES.SUM ||
                operation == COMPUTED_OPERATION_TYPES.MULTIPLY) && (
                <FormControl variant="outlined" size="small" color="primary">
                  <InputLabel id="select-option-label">Polarity</InputLabel>
                  <Select
                    style={{ minWidth: 60 }}
                    size="small"
                    color="primary"
                    labelId="select-option-label"
                    id="select-option"
                    value={param.polarity || '+ve'}
                    onChange={(e) => {
                      setParams(
                        params.map((it) => {
                          return it.id == param.id ? { ...param, polarity: e.target.value } : it
                        })
                      )
                    }}
                    label="Polarity"
                  >
                    {Object.keys(VALUE_POLARITY).map((it) => {
                      return <MenuItem value={it}>{it}</MenuItem>
                    })}
                  </Select>
                </FormControl>
              )}
            <IconButton
              onClick={() => {
                setParams(params.filter((it) => it.id != param.id))
              }}
            >
              <DeleteIcon size="small" />
            </IconButton>
          </div>
        )
      })}
      {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
        <>
          <p>Exclusions: </p>
          {exclusions.map((exclusion, index) => {
            return (
              <div
                style={{ display: 'flex', columnGap: 8, marginBottom: 16, alignItems: 'center' }}
              >
                <p style={{ margin: 0, minWidth: 20 }}>{index + 1}.</p>
                {/* Excluded Field */}
                <Autocomplete
                  value={getSelectedField(exclusion?.field?.id || null)}
                  id="autocomplete-field"
                  options={fields
                    .filter((it) => it.data_structure == FIELD_TYPES.LIST)
                    .map((it) => {
                      return { value: it, label: it.field_template_name }
                    })}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} label="Excluded Field" />}
                  onChange={(e, v) => {
                    setExclusions(
                      exclusions.map((it) => {
                        return it.id == exclusion.id ? { ...exclusion, field: v?.value } : it
                      })
                    )
                  }}
                  size="small"
                />
                <Autocomplete
                  value={getSelectedItem(
                    exclusion?.listItem?.id || null,
                    exclusion?.field?.list_id
                  )}
                  id="autocomplete-field"
                  options={getListItemOptions(exclusion?.field)}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} label="Field Value" />}
                  onChange={(e, v) => {
                    setExclusions(
                      exclusions.map((it) => {
                        return it.id == exclusion.id ? { ...exclusion, listItem: v?.value } : it
                      })
                    )
                  }}
                  size="small"
                />
                <IconButton
                  onClick={() => {
                    setExclusions(exclusions.filter((it) => it.id != exclusion.id))
                  }}
                >
                  <DeleteIcon size="small" />
                </IconButton>
              </div>
            )
          })}
        </>
      )}

      <AlternateComputationSetupUI
        altFieldTemplateId={altFieldTemplateId}
        setaltFieldTemplateId={setaltFieldTemplateId}
        fields={fields}
        field={field}
        listData={listData}
        altComputation={altComputation}
        setaltComputation={setaltComputation}
      />

      <div
        style={{
          display: 'flex',
          columnGap: 16,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={addAltComputation}
            style={{ marginTop: 16 }}
          >
            Add Alt Computation
          </Button>
        </div>

        <Button variant="outlined" color="primary" onClick={addParam} style={{ marginTop: 16 }}>
          Add Param
        </Button>

        {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={addExclusion}
            style={{ marginTop: 16 }}
          >
            Add Exclusion
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={saveComputation}
          style={{ marginTop: 16 }}
        >
          Save
        </Button>
      </div>
    </div>
  ) : (
    <div>
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Is Computed</InputLabel>
        <Select
          style={{ minWidth: 60 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={computed ? 'Yes' : 'No'}
          onChange={(e) => {
            setComputed(e.target.value == 'Yes')
          }}
          label="Polarity"
        >
          <MenuItem value={'No'}>No</MenuItem>
          <MenuItem value={'Yes'}>Yes</MenuItem>
        </Select>
      </FormControl>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={saveComputation}
          style={{ marginTop: 16 }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

export default FieldComputationSetupUI
